// src/components/patient/booking/HoldAndBook.jsx
//
// The review / hold / book / payment step.
//
//   Continue (ServiceBookingPanel) -> beginHold()  -- the ONE hold request
//   this step mounts with that in-flight `operation` and CONSUMES it:
//     pending  -> "Reserving your slot…" (no timer, no claim of a reservation)
//     success  -> countdown from the SERVER's expires_at/server_now
//     failure  -> conflict: back to refreshed times (alternatives)
//                 indeterminate (timeout/5xx): reconcile via the read-only
//                 current-hold GET, then the secured POST recovery; never a
//                 blind second hold
//   Confirm books with only the ids the hold response returned. Once an
//   appointment exists this step never calls the booking endpoint again:
//   payment retries call checkout only.
//
// Everything on the summary (doctor, clinic, duration, price, timezone,
// cancellation policy) comes from the hold response -- the held service
// VERSION -- with the discovery card only as a fallback for older servers.
//
// The raw hold token lives in a ref for as long as the hold does. It is never
// rendered, stored in web storage, put in a URL or logged.

import React, { useCallback, useEffect, useRef, useState } from "react";

import { apiErrorCode, readApiError } from "../../../lib/apiErrors";
import { describeCancellationPolicy } from "../../../lib/cancellationPolicy";
import { formatDateTimeInZone, formatLocalDate, formatTimeInZone } from "../../../lib/displayTimezone";
import {
  beginHold,
  clockOffsetMs,
  formatCountdown,
  isIndeterminate,
  reconcileHold,
  remainingSeconds,
} from "../../../lib/holdSession";
import { patientBookingApi } from "../../../services/patientBooking";

import { Icon, Pill, classNames, modeLabel, modeTone, money } from "./bookingUi";

const TAKEN_CODES = new Set(["SLOT_HELD", "SLOT_NOT_AVAILABLE", "DOCTOR_CONFLICT", "PATIENT_CONFLICT", "HOLD_CONTEXT_MISMATCH", "SERVICE_NOT_BOOKABLE"]);
const ENDED_CODES = new Set(["HOLD_EXPIRED", "HOLD_RELEASED", "HOLD_NOT_FOUND"]);

export function holdRequestFor(service, slot, displayTimezone, replaceExisting = false) {
  return {
    slotInventoryId: slot.slot_inventory_id,
    serviceVersionId: service.service_version_id,
    consultationMode: slot.consultation_mode,
    practiceLocationId: slot.practice_location_id,
    displayTimezone,
    replaceExisting,
  };
}

function SummaryRow({ label, children }) {
  return (
    <div className="flex flex-col gap-0.5 py-2 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
      <dt className="text-xs font-semibold uppercase tracking-wide text-muted">{label}</dt>
      <dd className="text-sm font-medium text-text sm:text-right">{children}</dd>
    </div>
  );
}

export default function HoldAndBook({ service, slot, displayTimezone, operation: initialOperation = null, recovered = null, onBack, onHoldChange, onBooked }) {
  // holding | reconciling | held | expired | failed | booking | booked
  const [phase, setPhase] = useState(recovered ? "held" : "holding");
  const [operation, setOperation] = useState(initialOperation);
  const [hold, setHold] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState("");
  const [retryKind, setRetryKind] = useState(null); // "create" | "reconcile"
  const [replaceNeeded, setReplaceNeeded] = useState(false);
  const [patientNote, setPatientNote] = useState("");
  const [bookedName, setBookedName] = useState("");
  const [appointment, setAppointment] = useState(null);
  const [paymentBusy, setPaymentBusy] = useState(false);
  const [paymentError, setPaymentError] = useState("");

  const tokenRef = useRef(null);
  const offsetRef = useRef(0);
  const bookingRef = useRef(false);
  const bookedRef = useRef(false);
  const busyRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const adopt = useCallback(
    (data, requestedAt) => {
      tokenRef.current = data.hold_token;
      offsetRef.current = clockOffsetMs(data.server_now, requestedAt);
      const left = remainingSeconds(data.expires_at, offsetRef.current);
      setHold(data);
      setError("");
      setRetryKind(null);
      setReplaceNeeded(false);
      setCountdown(left);
      setPhase(left > 0 ? "held" : "expired");
      onHoldChange?.({
        holdId: data.hold_id,
        release: () => {
          const token = tokenRef.current;
          tokenRef.current = null;
          return token ? patientBookingApi.releaseHold(data.hold_id, token) : Promise.resolve(null);
        },
      });
    },
    [onHoldChange]
  );

  const reconcile = useCallback(async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setPhase("reconciling");
    setError("");
    setRetryKind(null);
    try {
      const outcome = await reconcileHold(holdRequestFor(service, slot, displayTimezone));
      if (!mountedRef.current) return;
      if (outcome.outcome === "recovered") {
        adopt(outcome.data, outcome.operation.requestedAt);
      } else if (outcome.outcome === "other") {
        setPhase("failed");
        setReplaceNeeded(true);
        setError("You already have another time on hold.");
      } else {
        // The server confirms nothing is held for you: trying again is safe.
        setPhase("failed");
        setError("Your slot was not reserved. Your selection is kept — you can try again.");
        setRetryKind("create");
      }
    } catch (err) {
      if (!mountedRef.current) return;
      setPhase("failed");
      setError(readApiError(err, "We still couldn't confirm whether your slot is reserved."));
      setRetryKind("reconcile");
    } finally {
      busyRef.current = false;
    }
  }, [adopt, service, slot, displayTimezone]);

  const handleFailure = useCallback(
    (err) => {
      const code = apiErrorCode(err);
      if (code === "HOLD_REPLACEMENT_REQUIRED") {
        setReplaceNeeded(true);
        setPhase("failed");
        setError("You already have another time on hold.");
        return;
      }
      if (TAKEN_CODES.has(code)) {
        onBack?.({ refresh: true, message: readApiError(err, "That time is no longer available. Please choose another.") });
        return;
      }
      if (isIndeterminate(err)) {
        // A timeout does not prove the hold failed: look before retrying.
        reconcile();
        return;
      }
      setPhase("failed");
      setError(readApiError(err, "We could not reserve this time."));
      setRetryKind("create");
    },
    [onBack, reconcile]
  );

  // Consume the operation started by Continue (or by an explicit retry below).
  // Mounting never issues a request of its own.
  useEffect(() => {
    if (recovered) {
      adopt(recovered.data || recovered, recovered.requestedAt);
      return undefined;
    }
    if (!operation) return undefined;
    let current = true;
    setPhase("holding");
    operation.promise.then(
      (data) => current && mountedRef.current && adopt(data, operation.requestedAt),
      (err) => current && mountedRef.current && handleFailure(err)
    );
    return () => {
      current = false;
    };
    // Runs once per operation; adopt/handleFailure are stable for its lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operation, recovered]);

  function startNewHold(replaceExisting = false) {
    if (busyRef.current || phase === "holding") return;
    setError("");
    setRetryKind(null);
    setReplaceNeeded(false);
    setOperation(beginHold(holdRequestFor(service, slot, displayTimezone, replaceExisting)));
  }

  useEffect(() => {
    if (phase !== "held" && phase !== "booking") return undefined;
    const id = window.setInterval(() => {
      const left = remainingSeconds(hold?.expires_at, offsetRef.current);
      setCountdown(left);
      if (left <= 0 && !bookingRef.current) {
        tokenRef.current = null;
        onHoldChange?.(null);
        setPhase("expired");
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [phase, hold, onHoldChange]);

  async function confirmBooking() {
    if (!hold || bookingRef.current || bookedRef.current || phase !== "held" || countdown <= 0) return;
    bookingRef.current = true;
    setPhase("booking");
    setError("");
    try {
      const result = await patientBookingApi.book({
        holdId: hold.hold_id,
        holdToken: tokenRef.current,
        slotInventoryId: hold.slot?.slot_inventory_id || slot.slot_inventory_id,
        serviceVersionId: hold.service_version_id || service.service_version_id,
        patientBookingNote: patientNote,
        bookedForPatientName: bookedName,
      });
      bookedRef.current = true;
      tokenRef.current = null;
      onHoldChange?.(null);
      setAppointment(result);
      setPhase("booked");
    } catch (err) {
      const code = apiErrorCode(err);
      if (ENDED_CODES.has(code)) {
        tokenRef.current = null;
        onHoldChange?.(null);
        setPhase("expired");
        setError(readApiError(err, "Your hold has ended. Please choose a time again."));
      } else {
        // The same token may be presented again: a retried confirmation
        // returns the existing appointment instead of booking twice.
        setPhase("held");
        setError(readApiError(err, "The appointment could not be booked."));
      }
    } finally {
      bookingRef.current = false;
    }
  }

  async function startPayment() {
    if (!appointment || paymentBusy) return;
    setPaymentBusy(true);
    setPaymentError("");
    try {
      const checkout = await patientBookingApi.startCheckout(appointment.id);
      if (!checkout?.checkout_url) throw new Error("missing checkout url");
      window.location.assign(checkout.checkout_url);
    } catch (err) {
      setPaymentError(readApiError(err, "Payment could not be started. Your appointment is still reserved."));
      setPaymentBusy(false);
    }
  }

  const summarySlot = hold?.slot || slot;
  const payment = appointment?.payment || hold?.payment;
  const zone = hold?.slot?.display_timezone || displayTimezone;
  const reserving = phase === "holding" || phase === "reconciling";
  const expired = phase === "expired";
  const canConfirm = phase === "held" && countdown > 0;

  if (phase === "booked" && appointment) {
    const paymentRequired = appointment.booking_result === "payment_required";
    return (
      <section aria-labelledby="booking-result-title" data-testid="booking-result">
        <div className={classNames("rounded-3xl border p-5", paymentRequired ? "border-amber-300 bg-amber-50" : "border-emerald-300 bg-emerald-50")}>
          <h3 id="booking-result-title" className="flex items-center gap-2 text-lg font-semibold">
            {paymentRequired ? <Icon name="credit" className="h-5 w-5 text-amber-700" /> : <Icon name="check" className="h-5 w-5 text-emerald-700" />}
            {paymentRequired ? "Appointment reserved — payment required" : "Appointment booked successfully"}
          </h3>
          <p className="mt-1 text-sm">
            {appointment.service_name} with {appointment.doctor_name} on {formatDateTimeInZone(appointment.start_at, zone)} ({zone}).
          </p>
          <p className="mt-1 text-sm">Reference {appointment.appointment_number}</p>
          {payment ? <p className="mt-2 text-sm font-semibold">{payment.message}</p> : null}
          {paymentRequired && payment?.payment_deadline ? (
            <p className="mt-1 text-xs">Pay by {formatDateTimeInZone(payment.payment_deadline, zone)} to keep this time.</p>
          ) : null}
        </div>
        {paymentError ? <div role="alert" className="mt-3 rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{paymentError}</div> : null}
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          {paymentRequired ? (
            <button type="button" onClick={startPayment} disabled={paymentBusy} aria-busy={paymentBusy || undefined} className="btn-primary-gradient rounded-2xl px-5 py-3 text-sm font-semibold text-white disabled:opacity-60">
              {paymentBusy ? "Opening payment…" : paymentError ? "Retry payment" : "Pay now"}
            </button>
          ) : null}
          <button type="button" onClick={() => onBooked?.(appointment)} className="rounded-2xl border border-border bg-white px-5 py-3 text-sm font-semibold hover:bg-slate-50">
            View appointment
          </button>
        </div>
      </section>
    );
  }

  const policy = hold ? describeCancellationPolicy(hold.cancellation_policy, { currency: hold.currency || service.currency, startAt: summarySlot.start_at, timeZone: zone }) : null;
  const duration = hold?.duration_minutes ?? service.duration_min;

  return (
    <section aria-labelledby="hold-and-book-title" data-testid="hold-and-book">
      <h3 id="hold-and-book-title" className="text-sm font-semibold">Review and confirm</h3>

      {error ? <div role="alert" className="mt-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{error}</div> : null}

      {retryKind ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" onClick={() => (retryKind === "reconcile" ? reconcile() : startNewHold(false))} className="rounded-2xl bg-primary px-4 py-2 text-sm font-semibold text-white">
            {retryKind === "reconcile" ? "Check again" : "Try again"}
          </button>
        </div>
      ) : null}

      {replaceNeeded ? (
        <div className="mt-3 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm">
          <p>Holding this time will release the other time you are holding.</p>
          <div className="mt-3 flex gap-2">
            <button type="button" onClick={() => startNewHold(true)} className="rounded-2xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white">Hold this time instead</button>
            <button type="button" onClick={() => onBack?.({ refresh: false })} className="rounded-2xl border border-border bg-white px-4 py-2 text-sm font-semibold">Keep my other hold</button>
          </div>
        </div>
      ) : null}

      <div className={classNames("mt-4 rounded-2xl border p-4", expired ? "border-rose-200 bg-rose-50" : "border-border bg-white")} aria-live="polite">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted">Hold timer</div>
        <div className="mt-2 flex items-center gap-2 text-2xl font-semibold" data-testid="hold-countdown">
          {reserving ? (
            <>
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" aria-hidden="true" />
              <span className="text-base">{phase === "reconciling" ? "Checking your reservation…" : "Reserving your slot…"}</span>
            </>
          ) : expired ? "Expired" : phase === "failed" ? "—" : formatCountdown(countdown)}
        </div>
        <div className="mt-1 text-xs text-muted">
          {reserving
            ? "We're confirming this time with the clinic. It isn't reserved until this finishes."
            : expired
              ? "This hold has ended. Choose another time to see what is available now."
              : phase === "failed"
                ? "Your time is not reserved."
                : "Your time is reserved. Confirm before the timer ends."}
        </div>
      </div>

      <div className="mt-4 rounded-2xl border border-border bg-surface p-4" data-testid="booking-summary">
        <div className="text-base font-semibold">{hold?.service_name || service.service_name}</div>
        <dl className="mt-2 divide-y divide-border">
          {hold?.patient_name ? <SummaryRow label="Patient">{hold.patient_name}</SummaryRow> : null}
          <SummaryRow label="Doctor">{hold?.doctor_name || service.doctor_display_name || service.doctor_full_name}</SummaryRow>
          <SummaryRow label="Clinic">{hold?.clinic_name || service.clinic_name || "—"}</SummaryRow>
          <SummaryRow label="Date & time">
            {formatLocalDate(summarySlot.local_date)} · {formatTimeInZone(summarySlot.start_at, zone)} – {formatTimeInZone(summarySlot.end_at, zone)}
            <span className="block text-xs font-normal text-muted" data-testid="summary-timezone">Times in {zone}</span>
          </SummaryRow>
          {duration ? <SummaryRow label="Duration">{duration} minutes</SummaryRow> : null}
          <SummaryRow label="Consultation">
            <Pill tone={modeTone(summarySlot.consultation_mode)}>{modeLabel(summarySlot.consultation_mode)}</Pill>
          </SummaryRow>
          {summarySlot.location_name ? <SummaryRow label="Location">{summarySlot.location_name}</SummaryRow> : null}
          <SummaryRow label="Price">{money(hold?.price ?? service.price, hold?.currency || service.currency)}</SummaryRow>
        </dl>
        {payment ? <div className="mt-2 text-xs text-muted" data-testid="payment-requirement">{payment.message}</div> : null}
      </div>

      {policy ? (
        <div className="mt-4 rounded-2xl border border-border bg-white p-4" data-testid="confirm-cancellation-policy">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted">Cancellation policy</div>
          <p className="mt-1 text-sm font-medium">{policy.headline}</p>
          {policy.details.map((line) => (
            <p key={line} className="mt-1 text-xs text-muted">{line}</p>
          ))}
        </div>
      ) : null}

      {payment?.requirement === "required" && !expired ? (
        <div className="mt-4 rounded-2xl border border-violet-200 bg-violet-50 p-4 text-xs leading-5 text-violet-800">
          After confirming, you will be asked to pay online to secure this appointment.
        </div>
      ) : null}

      <div className="mt-4">
        <label htmlFor="booked-for-name" className="text-xs font-semibold">Booked for name (optional)</label>
        <input id="booked-for-name" maxLength={255} value={bookedName} onChange={(e) => setBookedName(e.target.value)} placeholder="Leave empty to use your account name" className="mt-2 w-full rounded-2xl border border-border bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
      </div>

      <div className="mt-4">
        <label htmlFor="patient-booking-note" className="text-xs font-semibold">Notes for doctor (optional)</label>
        <textarea id="patient-booking-note" maxLength={2000} value={patientNote} onChange={(e) => setPatientNote(e.target.value)} placeholder="Describe symptoms, reason for visit, or preference" className="mt-2 min-h-[120px] w-full rounded-2xl border border-border bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
      </div>

      <div className="sticky bottom-0 mt-5 flex flex-col gap-2 border-t border-border bg-white pt-3 sm:flex-row">
        <button type="button" onClick={() => onBack?.({ refresh: expired || phase === "failed", abandon: phase === "held" })} disabled={phase === "booking" || reserving} className="rounded-2xl border border-border bg-white px-5 py-3 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50">
          Choose another time
        </button>
        <button
          type="button"
          onClick={confirmBooking}
          disabled={!canConfirm}
          aria-busy={phase === "booking" || undefined}
          className={classNames("btn-primary-gradient flex-1 rounded-2xl px-5 py-3 text-sm font-semibold text-white", !canConfirm && "cursor-not-allowed opacity-60")}
        >
          {phase === "booking" ? "Booking…" : payment?.requirement === "required" ? "Confirm & continue to payment" : "Confirm appointment"}
        </button>
      </div>
    </section>
  );
}
